/**
 * ChatPanel — the chat surface component tree.
 *
 * Extracted verbatim from pages/Workspace.tsx (crucible plan 001 Step 5.4).
 * ChatPanel is a props-driven presentational component: it owns local UI
 * state (scroll fades, textarea multiline, voice capture) but consumes all
 * chat data + callbacks (messages, chatInput, onSend, hydration state,
 * interview slices, etc.) as props from Workspace, which owns the chat
 * brain (useChatSession). Also carries the ChatPanel-only helpers moved
 * alongside it: the speech-recognition shims, the enter-to-send /
 * typing-indicator predicates (re-exported from Workspace for tests), the
 * VisibilityStrip / ChatProgressEmptyState / AutoBuildProgressCard
 * sub-components, and the ChatTvSteeringNudge / ChatPanelPlaceholder. Moved
 * byte-for-byte; behavior preserved. No remount of the chat subtree — the
 * MountProbe guard in Workspace.live-preview-chat-surface.test.tsx stays green.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Mic, AlertTriangle, Paperclip } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Chip } from '../components/ui/chip';
import type {
  ActivitySnapshot,
  PhaseProgressUpdate,
} from '@volley/crucible-ipc-types/ipc-channels';
import type { PhaseBDecision } from '@volley/crucible-ipc-types/build-game-types';
import type { SerialisedError } from '@volley/crucible-ipc-types/error-types';
import type { AgentSendAttachment } from '@volley/crucible-ipc-types';
import type { AssetReviewItem, AssetReviewSnapshot } from '@volley/crucible-ipc-types';
import type { WorkspaceRunState, WorkspaceRunKind } from '../workspace/run-state';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { parseMarkdownQuestion } from './markdown-question';
import type { ActivityLiveness } from './activity-freshness';
import { resizeImageFile } from './resize-image';
import type { ChatMessage, ToolCallEntry } from './types';
import type { QueuedChatMessage } from './state';
import { CrucibleErrorState } from '../components/CrucibleErrorState';
import { ChatErrorBoundary } from '../components/ChatErrorBoundary';
import { ReviewAnswersModal } from '../components/ReviewAnswersModal';
import { BugDraftCard } from '../components/BugDraftCard';
import { ConflictResolutionCard } from '../components/ConflictResolutionCard';
import { AttachmentThumbnail } from '../components/AttachmentThumbnail';
import { AttachmentRedactionInfo } from '../components/AttachmentRedactionInfo';
import { IntegratedSoulInterview } from '../components/IntegratedSoulInterview';
import {
  AskUserQuestionCard,
  ChatBubble,
  MessageContent,
  PendingPhaseBDecisionCard,
  TypingIndicator,
} from './ChatMessageComponents';
import { voiceInputErrorMessage } from '../lib/voice-error';

type WorkspaceSpeechRecognitionAlternativeLike = {
  transcript: string;
};

type WorkspaceSpeechRecognitionResultLike = {
  readonly length: number;
  item(index: number): WorkspaceSpeechRecognitionAlternativeLike;
};

type WorkspaceSpeechRecognitionResultListLike = {
  readonly length: number;
  item(index: number): WorkspaceSpeechRecognitionResultLike;
};

type WorkspaceSpeechRecognitionEventLike = {
  readonly resultIndex: number;
  readonly results: WorkspaceSpeechRecognitionResultListLike;
};

type WorkspaceSpeechRecognitionErrorEventLike = {
  readonly error?: string;
  readonly message?: string;
};

type WorkspaceSpeechRecognitionLike = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: WorkspaceSpeechRecognitionEventLike) => void) | null;
  onerror: ((event: WorkspaceSpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
};

type WorkspaceSpeechRecognitionConstructor = new () => WorkspaceSpeechRecognitionLike;

type WorkspaceSpeechRecognitionWindow = Window &
  typeof globalThis & {
    SpeechRecognition?: WorkspaceSpeechRecognitionConstructor;
    webkitSpeechRecognition?: WorkspaceSpeechRecognitionConstructor;
  };

function workspaceSpeechRecognitionCtor(): WorkspaceSpeechRecognitionConstructor | null {
  if (typeof window === 'undefined') return null;
  const speechWindow = window as WorkspaceSpeechRecognitionWindow;
  return speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition ?? null;
}

/**
 * Decide whether the chat panel should render the typing indicator
 * underneath the assistant placeholder bubble. Issue #200: during
 * Foundry's `/build-game` Phase A interview the agent emits a cascade
 * of `tool_call` chunks (Edits to `.foundry/build-game-interview.json`)
 * BEFORE any narrative text. The indicator must remain visible while
 * the bubble has only tool-call activity — otherwise the user clicks
 * an answer and sees no signal that the agent is working.
 *
 * Contract:
 *   - `isAiTyping` must be true (the in-flight gate)
 *   - The active assistant bubble has no text content yet (toolCalls
 *     don't count — `m.content` is text-only by reducer design)
 *
 * Crucible #602 PR #C4 — `inline-checkpoint` rows are chrome, not
 * chat. The emitter (Issue A) can land a checkpoint AFTER an
 * assistant bubble has already streamed text, which would flip the
 * trailing-message inspection back to a non-assistant row and
 * spuriously show the indicator for the next render tick. Walk
 * backwards past checkpoint rows so the predicate reads the latest
 * actual chat turn — the indicator's behaviour is unchanged for
 * chat-only timelines.
 *
 * Pure helper exported for unit testing — the JSX gate calls through
 * to this so the predicate is the same in production and in tests.
 */
export function shouldShowTypingIndicator(
  messages: readonly ChatMessage[],
  isAiTyping: boolean,
): boolean {
  if (!isAiTyping) return false;
  // Walk backwards past `inline-checkpoint` chrome to find the most
  // recent chat row. Checkpoints don't carry the "the agent is
  // talking now" signal — they're durable records of past moments,
  // so they must not flip the typing indicator on or off.
  let lastChatMsg: ChatMessage | undefined;
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (!m) continue;
    if (m.role === 'inline-checkpoint') continue;
    lastChatMsg = m;
    break;
  }
  const hasText = lastChatMsg?.role === 'assistant' && lastChatMsg.content.length > 0;
  return !hasText;
}

/**
 * Decide whether a keydown on the chat textarea should fire `onSend`.
 * Issue #242 inverted the default: plain Enter sends, Shift+Enter
 * inserts a newline. Cmd/Ctrl+Enter is kept as a redundant accelerator
 * so users with muscle memory from the old binding still get a send
 * (and so any docs/screenshots referencing the old combo still work).
 *
 * Critical guard: `e.isComposing` (or `e.keyCode === 229`, the legacy
 * IME marker) means the user is mid-IME composition — e.g. converting
 * Japanese kana → kanji, or picking a Chinese candidate. Enter in that
 * state belongs to the IME, not us; firing send would chop the
 * composition off mid-word. We bail in that case and let the IME
 * handle the keystroke.
 *
 * Pure predicate exported for unit testing — the textarea handler
 * calls through to this so production and tests share one predicate.
 */
export function shouldSendOnEnter(
  e: Pick<
    React.KeyboardEvent<HTMLTextAreaElement>,
    'key' | 'shiftKey' | 'metaKey' | 'ctrlKey' | 'nativeEvent'
  > & { isComposing?: boolean },
): boolean {
  if (e.key !== 'Enter') return false;
  // React 18 surfaces `nativeEvent.isComposing`; some test harnesses
  // pass a synthetic with the flag at the top level. Check both.
  const composing =
    e.isComposing === true ||
    (e.nativeEvent as { isComposing?: boolean } | undefined)?.isComposing === true ||
    (e.nativeEvent as { keyCode?: number } | undefined)?.keyCode === 229;
  if (composing) return false;
  if (e.shiftKey) return false;
  // Plain Enter, or Cmd/Ctrl+Enter (redundant accelerator) → send.
  return true;
}

export function shouldShowFreshChatEmptyState(args: {
  messagesLength: number;
  isAiTyping: boolean;
  hasPinnedAskUserQuestion: boolean;
  isArtifactPaneActive: boolean;
  interviewSlotActive: boolean;
  runStateKind: WorkspaceRunKind;
}): boolean {
  if (args.runStateKind !== 'ready') return false;
  return (
    args.messagesLength === 0 &&
    !args.isAiTyping &&
    !args.hasPinnedAskUserQuestion &&
    (args.isArtifactPaneActive || !args.interviewSlotActive)
  );
}

export function shouldUsePassiveNorthStarChat(args: {
  newUi: boolean;
  northStarCreateInterviewActive?: boolean;
  soulInterviewGameId: string | null;
}): boolean {
  return (
    args.newUi && args.northStarCreateInterviewActive === true && args.soulInterviewGameId !== null
  );
}

export function hasFreshRunningActivitySnapshot(snapshot: ActivitySnapshot | null): boolean {
  if (!snapshot) return false;
  return Object.values(snapshot.byAgentId).some(
    (row) => row.status === 'running' && row.stale !== true,
  );
}

/**
 * Whether the auto-scroll-to-bottom effect has real content to scroll to,
 * extracted for crucible#3819.
 *
 * Args:
 *   input: The same values the effect's dependency array reads — a new
 *     chat message, the AI-typing indicator, a pinned ask-user-question, a
 *     pending Phase B decision, or an active interview slot all count as
 *     "content arrived."
 *
 * Returns:
 *   True once any of those triggers is present. The caller uses this to
 *   decide the scroll `behavior` ('instant' until the first render with
 *   content, 'smooth' after) — see that docblock at the call site for why:
 *   an async log load's first population must not animate.
 *
 * Raises:
 *   Never.
 */
export function chatAutoScrollHasContent(input: {
  messagesLength: number;
  isAiTyping: boolean;
  pinnedAskUserQuestion: unknown;
  pendingPhaseBDecision: unknown;
  interviewSlotActive: boolean;
}): boolean {
  return (
    input.messagesLength > 0 ||
    input.isAiTyping ||
    !!input.pinnedAskUserQuestion ||
    !!input.pendingPhaseBDecision ||
    input.interviewSlotActive
  );
}

/**
 * Checks whether derived progress has reached the terminal build phase.
 *
 * Args:
 *   phaseProgress: The latest phase progress payload, if one is available.
 *
 * Returns:
 *   True when derived progress has advanced to phase 7 or later.
 *
 * Raises:
 *   None.
 */
function isDerivedProgressComplete(phaseProgress: PhaseProgressUpdate | null | undefined): boolean {
  return phaseProgress?.kind === 'derived' && phaseProgress.derived.current >= 7;
}

/**
 * Derives the compact status row displayed above the chat composer.
 *
 * Args:
 *   runState: Durable Workspace-level run state for the active game.
 *   isAiTyping: Whether the active chat turn is still streaming.
 *   activitySnapshot: Latest agent activity snapshot, if one is available.
 *   phaseProgress: Latest derived build phase progress, if one is available.
 *   liveness: The SINGLE build-watch liveness verdict from `useActivityLiveness`
 *     (#3033/#3034) — client-relative and skew-immune. The status row branches
 *     on it directly instead of recomputing against its own clock.
 *
 * Returns:
 *   The status row label/tone, or null when no row should be shown.
 *
 * Raises:
 *   None.
 */
export function deriveChatStatusRow(
  runState: WorkspaceRunState,
  isAiTyping: boolean,
  activitySnapshot: ActivitySnapshot | null,
  phaseProgress: PhaseProgressUpdate | null | undefined,
  liveness: ActivityLiveness = 'unknown',
): { label: string; tone: 'active' | 'waiting' | 'warning' | 'idle' } | null {
  if (runState.kind === 'waiting-for-plan-review') {
    return { label: 'Review Build Plan...', tone: 'waiting' };
  }
  if (runState.kind === 'drafting-plan') {
    if (runState.debugSource.startsWith('auto-build:')) {
      return { label: 'Preparing build...', tone: 'active' };
    }
    return { label: 'Preparing Build Plan...', tone: 'active' };
  }
  if (runState.kind === 'waiting-on-user') {
    return { label: 'Waiting on your input...', tone: 'waiting' };
  }
  if (runState.kind === 'needs-attention') {
    return { label: 'Needs attention', tone: 'warning' };
  }
  if (
    runState.kind === 'building' &&
    phaseProgress?.kind === 'derived' &&
    !isDerivedProgressComplete(phaseProgress) &&
    !hasFreshRunningActivitySnapshot(activitySnapshot) &&
    !isAiTyping
  ) {
    // A poll-fed cloud build carries no attached activity snapshot, so the
    // absence of a live agent row is NOT proof the build is idle — the SPA
    // simply is not holding the chunk stream. lastActivityAt is that proof.
    // Two-tier because the stamp is turn-gated (see activity-freshness.ts):
    //   fresh   ⇒ actively streaming ⇒ "Working…"
    //   quiet   ⇒ alive but between turns ⇒ neutral "Build running…"
    //   unknown ⇒ no stamp (older pod / pre-turn) ⇒ neutral "Build running…"
    //   stalled ⇒ quiet past the turn-boundary window ⇒ honest stall warning
    // This retires the "Build running - no live agents" lie (#2347/#3025) and,
    // per rider 1, never brands an absent stamp as stalled. `liveness` is the
    // ONE client-relative verdict from `useActivityLiveness` (#3033/#3034).
    if (liveness === 'fresh') {
      return { label: 'Working...', tone: 'active' };
    }
    if (liveness === 'stalled') {
      return { label: 'Build stalled - no recent activity', tone: 'warning' };
    }
    return { label: 'Build running...', tone: 'waiting' };
  }
  if (
    isAiTyping ||
    runState.kind === 'starting-interview' ||
    runState.kind === 'interview-in-progress' ||
    runState.kind === 'build-kickoff-pending' ||
    runState.kind === 'building'
  ) {
    return { label: 'Working...', tone: 'active' };
  }
  return null;
}

/**
 * Phase 3 visibility strip — turn count + (optional) USD spend for
 * the last completed turn. Renders inline next to the AI Chat title.
 * Hidden when `spend === null` so a fresh chat / mid-turn state shows
 * nothing rather than a stale value from a previous turn.
 *
 * Cost is gated behind `showCost` (mirror of `coreConfig.showCostInChat`,
 * default false). Turn count is always shown when present — it's
 * activity, not money, and gives the user feedback that the agent
 * actually did something even when they don't care about cost.
 */
function VisibilityStrip({
  spend,
  showCost,
}: {
  spend: { totalCostUsd?: number; numTurns?: number } | null;
  showCost: boolean;
}): React.JSX.Element | null {
  if (!spend) return null;
  const turnsLabel =
    typeof spend.numTurns === 'number'
      ? `${spend.numTurns} turn${spend.numTurns === 1 ? '' : 's'}`
      : null;
  const costLabel =
    showCost && typeof spend.totalCostUsd === 'number'
      ? `$${spend.totalCostUsd.toFixed(spend.totalCostUsd >= 1 ? 2 : 3)}`
      : null;
  if (!turnsLabel && !costLabel) return null;
  return (
    <div
      className="flex items-center gap-2 text-[11px] tabular-nums text-white/40"
      data-testid="chat-visibility-strip"
    >
      {turnsLabel && (
        <span
          data-testid="chat-visibility-turns"
          title="Agent loop turns the SDK ran on the last message"
        >
          {turnsLabel}
        </span>
      )}
      {turnsLabel && costLabel && <span className="text-white/20">·</span>}
      {costLabel && (
        <span
          data-testid="chat-visibility-cost"
          title="Total USD spend reported by the SDK for the last turn"
        >
          {costLabel}
        </span>
      )}
    </div>
  );
}

function ChatProgressEmptyState({ runState }: { runState: WorkspaceRunState }): React.JSX.Element {
  return (
    <div
      className="flex h-full flex-col items-center justify-center gap-2 text-center"
      data-testid="chat-progress-empty-state"
    >
      <p className="text-sm font-medium text-white/70">{runState.label}</p>
      {runState.detail && (
        <p className="max-w-md text-xs leading-relaxed text-white/35">{runState.detail}</p>
      )}
    </div>
  );
}

export function autoBuildStatusFromRunState(runState: WorkspaceRunState): string | null {
  if (!runState.debugSource.startsWith('auto-build:')) return null;
  return runState.debugSource.split(':')[2] ?? null;
}

function AutoBuildProgressCard({
  runState,
  onCancel,
  onRetry,
}: {
  runState: WorkspaceRunState;
  onCancel: () => void;
  onRetry: () => void;
}): React.JSX.Element {
  const status = autoBuildStatusFromRunState(runState);
  const canCancel =
    status === 'armed' || status === 'generating-docs' || status === 'exporting-package';
  const canRetry = runState.kind === 'needs-attention' && runState.primaryCta === 'retry';
  return (
    <div
      className="mb-4 rounded-xl border border-white/10 bg-white/[0.035] px-4 py-3"
      data-testid="auto-build-progress-card"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-white/75">{runState.label}</p>
          {runState.detail && (
            <p className="mt-1 text-xs leading-relaxed text-white/40">{runState.detail}</p>
          )}
        </div>
        <span
          className={`mt-1 size-2 shrink-0 rounded-full ${
            runState.tone === 'error' || runState.tone === 'warning'
              ? 'bg-amber-300'
              : 'animate-pulse bg-crucible-accent'
          }`}
          aria-hidden="true"
        />
      </div>
      {(canCancel || canRetry) && (
        <div className="mt-3 flex justify-end gap-2">
          {canCancel && (
            <Button
              variant="secondary"
              size="sm"
              type="button"
              onClick={onCancel}
              className="rounded-md"
            >
              Cancel
            </Button>
          )}
          {canRetry && (
            <Button size="sm" type="button" onClick={onRetry} className="rounded-md">
              Retry setup
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * #2058 (Phase 2b) — the chat-stream nudge that REPLACES the old in-chat asset
 * approval card. The approval UI moved OFF the chat onto the TV-preview steering
 * surface (the big visual pane); the chat shows only this small pointer so the
 * approval isn't lost in the build agent's scrolling commentary. It carries NO
 * approval controls — it is NOT a mirror of the surface, just a wayfinder.
 */
export function ChatTvSteeringNudge(): React.JSX.Element {
  return (
    <div
      className="flex items-center gap-3 rounded-xl border border-crucible-accent/40 bg-crucible-accent/[0.06] px-4 py-3"
      data-testid="chat-tv-steering-nudge"
      role="status"
    >
      <span
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-crucible-accent/20 text-crucible-accent"
        aria-hidden="true"
      >
        ▶
      </span>
      <div className="min-w-0">
        <p className="text-[12.5px] font-semibold text-white/90">
          Review your game art on the preview
        </p>
        <p className="text-[11.5px] leading-snug text-white/55">
          Your art is ready to review on the preview pane. Pick the look and approve each piece
          there — the build keeps narrating here.
        </p>
      </div>
    </div>
  );
}

function ChatPanelImpl({
  gameId,
  gameName,
  messages,
  chatInput,
  isAiTyping,
  runState,
  activitySnapshot,
  phaseProgress,
  buildLiveness = 'unknown',
  onInputChange,
  onSend,
  onStop,
  queuedMessages,
  onUnqueueMessage,
  persistError,
  onDismissPersistError,
  hydrationStatus,
  hydrationError,
  hydrationLongRunning,
  onRetryHydration,
  pendingAttachments,
  attachmentPop,
  onAddAttachment,
  onRemoveAttachment,
  attachmentError,
  onDismissAttachmentError,
  answerError,
  onDismissAnswerError,
  lastTurnSpend,
  showCostInChat,
  onAnswerMarkdownQuestion,
  onRetryMessage,
  onCancelAutoBuild,
  onRetryAutoBuild,
  pinnedAskUserQuestion,
  pendingPhaseBDecision,
  interviewSlotActive,
  onInterviewRendererReady,
  interviewReadyHandshakeAttempt = 0,
  localSidecarsAvailable = true,
  isArtifactPaneActive,
  showReviewModal,
  onCloseReviewModal,
  assetReviewCardGameId,
  onAssetReviewDone,
  soulInterviewGameId,
  onSoulInterviewDone,
  northStarCreatePreviewActive = false,
  northStarCreateInterviewActive = false,
  compact = false,
}: {
  gameId: string | undefined;
  gameName: string;
  messages: ChatMessage[];
  chatInput: string;
  isAiTyping: boolean;
  runState: WorkspaceRunState;
  activitySnapshot: ActivitySnapshot | null;
  phaseProgress: PhaseProgressUpdate | null | undefined;
  /**
   * The SINGLE build-watch liveness verdict from `useActivityLiveness`
   * (#3033/#3034), computed once at the Workspace level (client-relative,
   * skew-immune) and passed down. Drives the build-watch status row so a
   * poll-fed build with no attached activity snapshot reads "Working…" (fresh)
   * rather than the old "no live agents" lie. Defaults to `unknown` for callers
   * that do not compute liveness.
   */
  buildLiveness?: ActivityLiveness;
  onInputChange: (v: string) => void;
  onSend: () => void;
  /**
   * Issue #140: invoked when the user clicks the Stop button while
   * the agent is mid-turn. The handler IPCs `agent:interrupt`; the
   * adapter emits an `interrupted` chunk back on `agent:stream`,
   * which is what actually clears the typing indicator and finalises
   * the assistant message bubble. The button itself only triggers
   * the request — it does NOT preemptively flip `isAiTyping`.
   */
  onStop: () => void;
  /**
   * Issue #164 follow-up: messages the user typed while the agent
   * was still mid-turn, queued in render order. Drained
   * automatically by the parent Workspace's effect when
   * `isAiTyping` flips false. Surfaced here as removable chips
   * above the input so the user can SEE what's pending and pull a
   * message back out before it fires. Since #2799 entries carry
   * their attachments too (capped at MAX_QUEUED_MESSAGES).
   */
  queuedMessages: readonly QueuedChatMessage[];
  /**
   * Remove a queued message by index. Used by the per-chip "x"
   * button so the user can change their mind before the queue
   * drains. The chip's index is stable across renders because new
   * messages are appended (push, not unshift).
   */
  onUnqueueMessage: (index: number) => void;
  persistError: SerialisedError | null;
  onDismissPersistError: () => void;
  /**
   * GAP-3 (cloud-vm/gui-parity-plan.md Phase 2) — honest hydration state.
   * `'loading'` renders a loading affordance (or the "preparing your
   * workspace" variant when `hydrationLongRunning`), `'error'` with a
   * retryable `hydrationError` renders a "Try again" error state, and
   * `'ready'` renders the normal timeline / fresh-empty state. A
   * non-retryable error (corrupt-history, category 'state') is left to the
   * existing `persistError` banner — `onRetryHydration` is not offered.
   */
  hydrationStatus: 'loading' | 'ready' | 'error';
  hydrationError: SerialisedError | null;
  hydrationLongRunning: boolean;
  onRetryHydration: () => void;
  /**
   * Screenshot-paste MVP (#193). Images the user has pasted into the
   * input but not yet sent. Rendered as removable chips above the
   * textarea; consumed by the next `onSend`.
   */
  pendingAttachments: readonly AgentSendAttachment[];
  /** Briefly true after an in-editor select-to-chat add — pops the chips row. */
  attachmentPop?: boolean;
  onAddAttachment: (a: AgentSendAttachment) => void;
  onRemoveAttachment: (index: number) => void;
  /** Last attachment-cap rejection message; null when no error. */
  attachmentError: string | null;
  onDismissAttachmentError: () => void;
  /**
   * #3060 — last FAILED composer gate-answer message; null when no error.
   * Set when the answer-the-pinned-question invoke rejects or resolves
   * `{ ok: false }` (the typed text is preserved). Optional so existing
   * ChatPanel call sites that never open a gate need not thread it.
   */
  answerError?: string | null;
  onDismissAnswerError?: () => void;
  /**
   * Phase 3 visibility strip — last completed turn's SDK-reported
   * cost / turn count. `null` while a turn is in flight or if the
   * SDK didn't include the fields. Cost is rendered only when
   * `showCostInChat` is enabled — turn count is always shown.
   */
  lastTurnSpend: { totalCostUsd?: number; numTurns?: number } | null;
  /** Mirror of `coreConfig.showCostInChat`. Off by default. */
  showCostInChat: boolean;
  /**
   * Issue #215 — fallback parser. Invoked when the user clicks one
   * of the synthetic option buttons rendered below an assistant
   * bubble whose markdown matched the `Question N of M:` drift
   * shape. The handler dispatches the option text through the same
   * `user/type` + `user/send` path the textarea uses.
   */
  onAnswerMarkdownQuestion: (optionText: string) => void;
  /**
   * Issue #369 — chat-bubble retry handler. Invoked when the user
   * clicks the "Try again" button on an error bubble whose
   * `retryable: true` flag was set by the structured error from main
   * (PR #368). The bubble passes its `ChatMessage` so the parent's
   * reducer dispatch can locate the preceding user input and re-fire
   * the send.
   */
  onRetryMessage: (message: ChatMessage) => void;
  onCancelAutoBuild: () => void;
  onRetryAutoBuild: () => void;
  pinnedAskUserQuestion: ToolCallEntry | null;
  pendingPhaseBDecision: PhaseBDecision | null;
  interviewSlotActive: boolean;
  onInterviewRendererReady: () => void;
  /** Workspace retry attempt that requests one fresh renderer-ready handshake. */
  interviewReadyHandshakeAttempt?: number;
  localSidecarsAvailable?: boolean;
  /** When true, the artifact pane owns InterviewSlotOverlay rendering. */
  isArtifactPaneActive: boolean;
  showReviewModal: boolean;
  onCloseReviewModal: () => void;
  /**
   * #2058 — in-chat asset-review card. Non-null when the integrated flag is ON
   * and the build task fired `asset-review:ready`. Renders `ChatAssetReviewCard`
   * inline in the message stream; `BuildReviewOverlay` is suppressed while set.
   */
  assetReviewCardGameId: string | null;
  /** Clears the in-chat review card once the engine reports done. */
  onAssetReviewDone: () => void;
  /**
   * #2058 — in-chat adaptive interview. Non-null while a soul-intake run is active for
   * this game; hosts IntegratedSoulInterview inline in the message stream.
   */
  soulInterviewGameId: string | null;
  /** Clears the in-chat interview once the run reports done. */
  onSoulInterviewDone: () => void;
  /** True while the north-star PreviewOverlay owns interview/build-progress create UI. */
  northStarCreatePreviewActive?: boolean;
  /** True ONLY during the card-driven north-star INTERVIEW (soul-intake) phase.
   *  Keeps the chat passive (composer suppressed; the in-stream interview drives
   *  input) while questions are being asked. False during BUILD/plan/review, so the
   *  composer is live there — parity with main's chat, where you can steer the agent
   *  mid-build. */
  northStarCreateInterviewActive?: boolean;
  /**
   * Develop side-by-side mode: the chat is in a narrow panel, so the
   * message column + composer drop their wide centred `max-w-3xl` and use
   * the full panel width with tighter horizontal padding.
   */
  compact?: boolean;
}): React.JSX.Element {
  const { newUi } = useFeatureFlags();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // The first auto-scroll (opening a game with an existing log) jumps instantly
  // — animating a long log from top to bottom on open is jarring. Live updates
  // after that animate smoothly.
  const didInitialScrollRef = useRef(false);
  // Scroll-affordance fades: show a top fade when there's log above the view
  // and a bottom fade when there's more below, so it's clear the log scrolls.
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const [chatScrollEdges, setChatScrollEdges] = useState({ top: false, bottom: false });
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const speechRecognitionRef = useRef<WorkspaceSpeechRecognitionLike | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // showReviewModal / onCloseReviewModal are lifted to the parent Workspace
  // so WorkspaceRightPanel's onEditDiscovery can trigger the modal from
  // outside ChatPanel's scope.
  // Tracks whether the textarea content has wrapped beyond one line.
  // When true the input renders as an expanding rectangle with a bottom
  // toolbar; when false it stays as the compact pill layout.
  const [isMultiLine, setIsMultiLine] = useState(false);
  const [answeredPhaseBDecisionId, setAnsweredPhaseBDecisionId] = useState<string | null>(null);
  // Baseline scrollHeight for a single line of text — measured the first
  // time the textarea is empty so we can detect wrapping reliably.
  const singleLineHeightRef = useRef<number>(0);
  // The composer corner radius is pinned to HALF the single-line field height
  // so the expanded (multi-line) box keeps the exact same corner curvature as
  // the original single-line pill instead of re-rounding to a taller capsule
  // as it grows. We measure the single-line wrapper (font/zoom-proof) and
  // reuse the value as a fixed radius on the expanded box.
  const composerWrapRef = useRef<HTMLDivElement>(null);
  const [pillRadius, setPillRadius] = useState<number | null>(null);
  const [voiceListening, setVoiceListening] = useState(false);
  // #2992 sweep — voice-input failure surfaced to the user (mic denied/
  // missing); null = no composer error banner shown.
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [voiceSupported] = useState(() => workspaceSpeechRecognitionCtor() !== null);

  const handleVoiceInput = useCallback(() => {
    if (voiceListening) {
      speechRecognitionRef.current?.stop();
      setVoiceListening(false);
      return;
    }
    setVoiceError(null);
    const Recognition = workspaceSpeechRecognitionCtor();
    if (!Recognition) {
      textareaRef.current?.focus();
      return;
    }
    speechRecognitionRef.current?.abort();
    const recognition = new Recognition();
    speechRecognitionRef.current = recognition;
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.onresult = (event) => {
      // #2996 Codex round 2 — a superseded instance's queued callbacks must
      // not touch shared state while a newer attempt is live.
      if (speechRecognitionRef.current !== recognition) return;
      let transcript = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results.item(i);
        if (result.length === 0) continue;
        transcript += result.item(0).transcript;
      }
      const spoken = transcript.trim();
      if (!spoken) return;
      const existing = chatInput.trim();
      onInputChange(existing ? `${existing} ${spoken}` : spoken);
    };
    recognition.onerror = (event) => {
      if (speechRecognitionRef.current !== recognition) return;
      setVoiceListening(false);
      // #2992 sweep — a denied/missing microphone used to fail silently here.
      const message = voiceInputErrorMessage(event.error, 'type your message instead');
      if (message) {
        setVoiceError(message);
        textareaRef.current?.focus();
      }
    };
    recognition.onend = () => {
      if (speechRecognitionRef.current !== recognition) return;
      setVoiceListening(false);
    };
    try {
      recognition.start();
      setVoiceListening(true);
    } catch {
      setVoiceListening(false);
      textareaRef.current?.focus();
    }
  }, [chatInput, onInputChange, voiceListening]);

  useEffect(
    () => () => {
      speechRecognitionRef.current?.abort();
      speechRecognitionRef.current = null;
    },
    [],
  );

  useEffect(() => {
    // React Router reuses ChatPanel across games; each game gets one instant hydration scroll.
    didInitialScrollRef.current = false;
  }, [gameId]);

  // Auto-scroll to bottom when new messages or interview prompts arrive.
  useEffect(() => {
    // The initial population — opening a game whose log loads async (often
    // []→[full log] in one update) — must NOT animate. Stay 'instant' until
    // the first render that actually has content; only live updates after that
    // animate smoothly. (Flipping on every run would let the async log load —
    // the 2nd run — animate the long scroll, which is what users were seeing.)
    // See chatAutoScrollHasContent's docblock (crucible#3819) for why each
    // of these triggers counts as "content arrived."
    const hasContent = chatAutoScrollHasContent({
      messagesLength: messages.length,
      isAiTyping,
      pinnedAskUserQuestion,
      pendingPhaseBDecision,
      interviewSlotActive,
    });
    const behavior: ScrollBehavior = didInitialScrollRef.current ? 'smooth' : 'instant';
    if (hasContent) didInitialScrollRef.current = true;
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, [
    messages,
    isAiTyping,
    pinnedAskUserQuestion,
    pendingPhaseBDecision,
    interviewSlotActive,
    runState.debugSource,
    runState.detail,
  ]);

  // Recompute the scroll-fade affordances (content above / below the view).
  const updateChatScrollEdges = useCallback(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    const top = el.scrollTop > 4;
    const bottom = el.scrollTop + el.clientHeight < el.scrollHeight - 4;
    setChatScrollEdges((prev) =>
      prev.top === top && prev.bottom === bottom ? prev : { top, bottom },
    );
  }, []);
  useEffect(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    updateChatScrollEdges();
    el.addEventListener('scroll', updateChatScrollEdges, { passive: true });
    const ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateChatScrollEdges) : null;
    ro?.observe(el);
    return () => {
      el.removeEventListener('scroll', updateChatScrollEdges);
      ro?.disconnect();
    };
  }, [updateChatScrollEdges]);
  // Content changes (new messages, interview prompts) change scrollHeight, so
  // re-evaluate the fades alongside the auto-scroll.
  useEffect(() => {
    updateChatScrollEdges();
  }, [
    updateChatScrollEdges,
    messages,
    isAiTyping,
    pinnedAskUserQuestion,
    pendingPhaseBDecision,
    interviewSlotActive,
  ]);

  useEffect(() => {
    if (!pendingPhaseBDecision || pendingPhaseBDecision.intentId !== answeredPhaseBDecisionId) {
      setAnsweredPhaseBDecisionId(null);
    }
  }, [answeredPhaseBDecisionId, pendingPhaseBDecision]);

  // Auto-resize textarea + detect multi-line wrapping.
  // useLayoutEffect (not useEffect) so the height is recomputed BEFORE the
  // browser paints. With a post-paint useEffect, the keystroke that adds a new
  // row painted once at the OLD height (textarea overflowing / a scrollbar
  // flashing) and then snapped to the taller box on the next frame — the jump
  // seen when text fills the 3rd line. Measuring + resizing pre-paint makes the
  // box grow by exactly one row in a single frame.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      const scrollH = el.scrollHeight;
      // Capture single-line baseline whenever the field is empty
      if (chatInput === '') {
        singleLineHeightRef.current = scrollH;
        setIsMultiLine(false);
      } else if (singleLineHeightRef.current > 0 && scrollH > singleLineHeightRef.current) {
        // Latch ON once the content wraps — and intentionally do NOT switch
        // back to the single-line pill while the field still has text. The
        // pill and the expanded rectangle give the textarea different widths,
        // so re-measuring after every keystroke near the wrap boundary made
        // the layout flip-flop (pill ↔ rectangle) on each character. Staying
        // expanded until the field is cleared keeps the box stable; the empty
        // branch above resets it to the pill when the input is sent/cleared.
        setIsMultiLine(true);
      }
      el.style.height = `${Math.min(scrollH, 160)}px`;
    }
  }, [chatInput]);

  // When the layout switches (pill ↔ rectangle), the textarea is remounted.
  // Re-focus and re-apply the correct height so the cursor stays and the box
  // is sized properly without requiring the user to click again. Layout effect
  // (pre-paint) so the freshly-mounted textarea never paints once at its
  // default 1-row height before snapping to the measured height.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    // Move cursor to end after remount
    const len = el.value.length;
    el.setSelectionRange(len, len);
    el.focus();
  }, [isMultiLine]);

  // Capture the single-line field height (→ half = the fixed corner radius for
  // the expanded box). Measured only while the single-line pill is mounted; the
  // value is constant for one line, so this settles on mount and re-confirms
  // whenever the field collapses back.
  useLayoutEffect(() => {
    if (isMultiLine) return;
    const el = composerWrapRef.current;
    if (el && el.offsetHeight > 0) setPillRadius(el.offsetHeight / 2);
  }, [isMultiLine]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (shouldSendOnEnter(e)) {
      e.preventDefault();
      onSend();
    }
  };

  /**
   * Screenshot-paste MVP (#193) + auto-resize (#212). Pull image
   * entries out of the paste's `clipboardData.items`, run them
   * through `resizeImageFile` (which decides whether to downscale
   * via `<canvas>` based on dimension / byte-size thresholds, then
   * base64-encodes the result), and dispatch into the reducer. Each
   * image fires its own `onAddAttachment` so the cap-check in the
   * reducer can reject one without losing the others.
   *
   * Issue #212 motivation: a Mac retina screenshot at 2880×1656 weighs
   * ~4-5 MB raw, slips past our 5 MB renderer cap, then Anthropic's
   * vision API rejects it server-side and the agent reports back
   * "I couldn't see it as it was too large" — no actionable next
   * step for the user. Resizing to 1920 px on the longest side
   * before encoding keeps the screenshot useful, dodges the
   * server-side rejection, and saves cost. The resize chain
   * preserves MIME (PNG → PNG, JPEG → JPEG, WebP → WebP) and falls
   * through to the original bytes on any decode failure so the
   * pre-fix behaviour is the worst case.
   *
   * `e.preventDefault()` runs only when at least one image was found —
   * we don't want to block a normal text paste. The async resize step
   * means `preventDefault` has to happen in the synchronous part of
   * the handler; that's fine because the textarea's text-paste branch
   * is a separate code path the browser handles natively.
   */
  // Shared image-file ingest for BOTH paste and drag-drop (#2268). Filters to
  // images, resizes, and dispatches each as an attachment. Fire-and-forget —
  // `resizeImageFile` handles its own decode/encode failures and falls back to
  // the original bytes so the reducer always sees a usable attachment.
  const ingestImageFiles = useCallback(
    (files: File[]): void => {
      for (const file of files) {
        if (!file.type.startsWith('image/')) continue;
        void resizeImageFile(file)
          .then((result) => {
            onAddAttachment({
              mimeType: result.mimeType,
              base64: result.base64,
              byteLength: result.byteLength,
              ...(result.didResize
                ? {
                    resizedFrom: {
                      width: result.originalWidth,
                      height: result.originalHeight,
                      finalWidth: result.finalWidth,
                      finalHeight: result.finalHeight,
                    },
                  }
                : {}),
            });
          })
          .catch((err: unknown) => {
            console.error('[chat] image attach failed', err);
          });
      }
    },
    [onAddAttachment],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>): void => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const images: File[] = [];
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (it && it.kind === 'file' && it.type.startsWith('image/')) {
          const f = it.getAsFile();
          if (f) images.push(f);
        }
      }
      if (images.length === 0) return;
      e.preventDefault();
      ingestImageFiles(images);
    },
    [ingestImageFiles],
  );

  // #2268 — drag-and-drop image files onto the composer (parity with the +
  // picker and paste). The attachment display + send pipeline is mode-agnostic,
  // so this works for local and cloud previews alike once the cloud transport
  // carries attachments.
  const handleComposerDragOver = useCallback((e: React.DragEvent<HTMLDivElement>): void => {
    // Only claim the drop when files are being dragged — let text / other drags
    // fall through to default handling.
    if (e.dataTransfer?.types?.includes('Files')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  }, []);

  const handleComposerDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>): void => {
      const files = e.dataTransfer?.files;
      if (!files || files.length === 0) return;
      // Claim the drop for ANY file payload (matching the dragOver guard) so a
      // non-image / mixed drop can't fall through to the browser default and
      // navigate the webview to the dropped file. Only images are ingested.
      e.preventDefault();
      const images = Array.from(files).filter((f) => f.type.startsWith('image/'));
      if (images.length === 0) return;
      ingestImageFiles(images);
    },
    [ingestImageFiles],
  );

  // #3034 — no per-consumer heartbeat here anymore: `buildLiveness` is the ONE
  // client-relative verdict computed upstream by `useActivityLiveness`, whose
  // single clock re-renders Workspace (and so this panel via the prop) as
  // wall-clock advances even with NO new poll data. The row just branches on it.
  const statusRow = deriveChatStatusRow(
    runState,
    isAiTyping,
    activitySnapshot,
    phaseProgress,
    buildLiveness,
  );
  // Fresh-game empty state: no messages, no active prompt, and no durable
  // run/progress state. Once phase progress says the game has started,
  // the main pane must not regress to the first-pitch prompt.
  // GAP-3 (cloud-vm/gui-parity-plan.md Phase 2) — honest hydration gates.
  // These take precedence over the fresh-empty / progress-empty states while
  // hydration is still pending or has errored, so a slow clone or a transient
  // transport failure reads as "loading" / "couldn't load", not an
  // indistinguishable blank "start a conversation" panel.
  //
  // The LOADING overlay shows only when there is genuinely nothing to display
  // yet: status 'loading' AND no messages AND no in-flight turn. The
  // `messages.length === 0 && !isAiTyping` guard is load-bearing — without it a
  // live/streaming turn (or a hydration that hasn't yet flipped to 'ready', e.g.
  // cookie-mode where `agent:get-status` resolves only once GAP-1 is live) would
  // be hidden behind the spinner, wedging the chat at "Loading…" while text is
  // actually streaming (regressed the cookie-mode `agent.send@1` e2e, PR #2026).
  // NOTE the asymmetry with the error gate below: the retry gate is status-ONLY
  // (no `messages.length` guard) because a drained `activity:checkpoint` row must
  // NOT suppress the "Try again" affordance (Cursor Bugbot, PR #2026); a live
  // turn never coincides with a terminal error, so only the loading gate needs
  // the message/typing guard.
  const showHydrationLoading =
    hydrationStatus === 'loading' && messages.length === 0 && !isAiTyping;
  // Only retryable transport errors get the in-panel "Try again" error state.
  // Corrupt-history (CrucibleError category 'state', retryable:false) is left
  // to the existing `persistError` recovery banner above — unchanged — so we
  // do NOT double up an error UI on it.
  const showHydrationRetryError =
    hydrationStatus === 'error' &&
    hydrationError?.retryable === true &&
    hydrationError.category !== 'state';
  const hydrationStateActive = showHydrationLoading || showHydrationRetryError;
  // Passive (composer suppressed; the in-stream soul interview drives input) ONLY when
  // that integrated interview is actually mounted. Cloud quick-interview projects use the
  // runtime-pushed InterviewSlotOverlay instead, so they must keep both that overlay and
  // the composer mounted while the north-star interview phase is active.
  // (#2314 Foundry-rail regression: this previously keyed off northStarCreatePreviewActive,
  // which spans the whole create→build flow, hiding the composer for the entire build.)
  const passiveNorthStarChat = shouldUsePassiveNorthStarChat({
    newUi,
    northStarCreateInterviewActive,
    soulInterviewGameId,
  });

  const isEmpty =
    !passiveNorthStarChat &&
    !hydrationStateActive &&
    shouldShowFreshChatEmptyState({
      messagesLength: messages.length,
      isAiTyping,
      hasPinnedAskUserQuestion: pinnedAskUserQuestion !== null,
      isArtifactPaneActive,
      interviewSlotActive,
      runStateKind: runState.kind,
    });
  const autoBuildProgressActive = autoBuildStatusFromRunState(runState) !== null;
  // Suppress "Answer the quick build-game questions." placeholder whenever the
  // integrated soul interview is mounted — it owns that region and shows its
  // own "Shaping the next question…" spinner during the LLM thinking pause.
  const showProgressEmptyState =
    newUi &&
    !isEmpty &&
    !hydrationStateActive &&
    messages.length === 0 &&
    runState.kind !== 'ready' &&
    !autoBuildProgressActive &&
    !pinnedAskUserQuestion &&
    !soulInterviewGameId &&
    !northStarCreatePreviewActive &&
    (isArtifactPaneActive || !interviewSlotActive);
  const showPendingPhaseBDecisionCard =
    newUi && !isArtifactPaneActive && !pinnedAskUserQuestion && pendingPhaseBDecision !== null;
  const handlePhaseBDecisionAnswer = useCallback(
    (label: string) => {
      if (!pendingPhaseBDecision || answeredPhaseBDecisionId === pendingPhaseBDecision.intentId) {
        return;
      }
      setAnsweredPhaseBDecisionId(pendingPhaseBDecision.intentId);
      onAnswerMarkdownQuestion(label);
    },
    [answeredPhaseBDecisionId, onAnswerMarkdownQuestion, pendingPhaseBDecision],
  );

  return (
    <section
      className={`flex flex-1 flex-col overflow-hidden${newUi && isEmpty ? ' justify-center' : ''}`}
      style={{ background: 'var(--color-crucible-bg)' }}
    >
      {/* Crucible #936 — defence-in-depth error boundary. */}
      <ChatErrorBoundary gameId={gameId}>
        {/* Chat header — legacy UI only. New UI removed this bar; the
          game title lives in WorkspaceHeader and preview controls
          moved to the workspace header as well. */}
        {!newUi && (
          <div className="flex items-center justify-between border-b border-white/10 px-4 py-2.5">
            <h2 className="text-sm font-medium text-white/60">Build &mdash; {gameName}</h2>
            <div className="flex items-center gap-3">
              <VisibilityStrip spend={lastTurnSpend} showCost={showCostInChat} />
            </div>
          </div>
        )}
        {persistError && (
          <div className="mx-4 mt-3" data-testid="chat-persist-error">
            <CrucibleErrorState error={persistError} compact />
            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={onDismissPersistError}
              className="mt-1 h-auto p-0 text-xs text-red-300/70 underline-offset-2 hover:underline font-normal"
              data-testid="chat-persist-error-dismiss"
            >
              Dismiss
            </Button>
          </div>
        )}

        {/* Messages — in new-UI mode, hidden when empty so IPC subscriptions stay active. */}
        <div className={newUi && isEmpty ? 'hidden' : 'relative flex min-h-0 flex-1 flex-col'}>
          {/* Top fade — content scrolled above the view. */}
          <div
            aria-hidden
            className={`pointer-events-none absolute inset-x-0 top-0 z-10 h-10 bg-gradient-to-b from-[var(--color-crucible-bg)] to-transparent transition-opacity duration-200 ${
              chatScrollEdges.top ? 'opacity-100' : 'opacity-0'
            }`}
          />
          <div ref={chatScrollRef} className="flex min-h-0 flex-1 flex-col overflow-auto">
            <div
              className={
                compact
                  ? 'flex w-full flex-1 flex-col px-4 py-6'
                  : 'mx-auto flex max-w-3xl flex-1 flex-col px-6 py-8'
              }
            >
              {/* GAP-3 (cloud-vm/gui-parity-plan.md Phase 2) — loading
                  affordance. Quick "Loading chat history…" by default; once
                  the load has been pending past the threshold (a cloud
                  first-touch git clone can take ~120s) it upgrades to an
                  explicit "preparing your workspace" message so the blank
                  window reads as provisioning, not a hang. Spinner + label
                  per the AGENTS-PROJECT.md loading-state visual spec. */}
              {showHydrationLoading && (
                <div
                  className="flex h-full flex-col items-center justify-center gap-3"
                  data-testid="chat-hydration-loading"
                  aria-busy="true"
                >
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/[0.12] border-t-white/50" />
                  <p className="text-center text-[15px]" style={{ color: 'var(--text-2)' }}>
                    {hydrationLongRunning ? 'Preparing your workspace…' : 'Loading chat history…'}
                  </p>
                  {hydrationLongRunning && (
                    <p
                      className="max-w-md text-center text-[13px]"
                      style={{ color: 'var(--text-3)' }}
                    >
                      First open can take a moment while we set things up.
                    </p>
                  )}
                </div>
              )}

              {/* GAP-3 — retryable hydration error. Corrupt-history
                  (non-retryable, category 'state') is handled by the
                  persistError banner above, so this block only ever shows
                  for transport-class failures the user can retry. */}
              {showHydrationRetryError && (
                <div
                  className="flex h-full flex-col items-center justify-center gap-3"
                  data-testid="chat-hydration-error"
                >
                  <p className="text-center text-[15px]" style={{ color: 'var(--text-2)' }}>
                    Couldn&rsquo;t load chat history
                  </p>
                  <p
                    className="max-w-md text-center text-[13px]"
                    style={{ color: 'var(--text-3)' }}
                  >
                    {hydrationError?.message ?? 'A connection problem interrupted hydration.'}
                  </p>
                  <Button
                    variant="secondary"
                    size="sm"
                    type="button"
                    onClick={onRetryHydration}
                    data-testid="chat-hydration-retry"
                    className="mt-1 rounded-full border-crucible-accent/40 bg-crucible-accent/10 text-crucible-accent-hover hover:border-crucible-accent/70 hover:bg-crucible-accent/20 hover:text-white"
                  >
                    Try again
                  </Button>
                </div>
              )}

              {isEmpty && (
                <div className="flex h-full flex-col items-center justify-center gap-3">
                  <div className="text-4xl text-white/10">&#9993;</div>
                  <p className="max-w-md text-center text-sm text-white/30">
                    Start a conversation with the AI to build your game.
                    <br />
                    <span className="text-white/20">
                      Press Enter to send · Shift+Enter for newline.
                    </span>
                  </p>
                </div>
              )}

              {showProgressEmptyState && <ChatProgressEmptyState runState={runState} />}

              {/* GAP-3 — while a hydration loading/error state is showing,
                  suppress the message list + typing indicator. Status is
                  'loading'/'error' only when there is no successfully-loaded
                  timeline; the only rows `messages` can hold here are stray
                  buffered `activity:checkpoint` pushes drained on a failed
                  load, which must not paint beneath the spinner / retry UI
                  (Cursor Bugbot, PR #2026). A successful load flips status to
                  'ready', so the real timeline renders normally. */}
              {!hydrationStateActive &&
                messages.map((msg) => (
                  <ChatBubble
                    key={msg.id}
                    message={msg}
                    onAnswerMarkdownQuestion={onAnswerMarkdownQuestion}
                    onRetry={onRetryMessage}
                    newUi={newUi}
                    pinnedAskUserQuestionId={pinnedAskUserQuestion?.id ?? null}
                  />
                ))}

              {!hydrationStateActive && shouldShowTypingIndicator(messages, isAiTyping) && (
                <div className="mb-3 flex justify-start" data-testid="chat-typing-indicator">
                  <div className="glint-thinking rounded-2xl rounded-bl-sm bg-crucible-surface px-4 py-3 text-sm text-white/70">
                    <TypingIndicator />
                  </div>
                </div>
              )}

              {newUi && autoBuildProgressActive && !northStarCreatePreviewActive && (
                <AutoBuildProgressCard
                  runState={runState}
                  onCancel={onCancelAutoBuild}
                  onRetry={onRetryAutoBuild}
                />
              )}

              {/* #2058 — the adaptive soul-intake interview, hosted INLINE in the chat
                  (Sean's vision: one continuous conversation). Active while the run is in
                  flight for this game; clears on done → create continues to build + review. */}
              {soulInterviewGameId && (
                <div className="mb-4" data-testid="workspace-soul-interview-mount">
                  <IntegratedSoulInterview
                    gameId={soulInterviewGameId}
                    onDone={onSoulInterviewDone}
                  />
                </div>
              )}

              {/* #2058 (Phase 2b) — the asset approval is no longer in the chat.
                  It moved OFF the chat onto the TV-preview steering surface (the
                  big visual pane), so it doesn't compete with the build agent's
                  scrolling commentary. The chat shows ONLY a small nudge pointing
                  at the surface — the approval UI itself is NOT mirrored here.
                  Gated on assetReviewCardGameId ONLY (the prop is already null
                  unless interviewIntegrated; see the parent pass). */}
              {assetReviewCardGameId && (
                <div className="mb-4" data-testid="chat-asset-review-mount">
                  <ChatTvSteeringNudge />
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
            {/* end max-w-3xl column */}
          </div>
          {/* Bottom fade — more content below the view. */}
          <div
            aria-hidden
            className={`pointer-events-none absolute inset-x-0 bottom-0 z-10 h-10 bg-gradient-to-t from-[var(--color-crucible-bg)] to-transparent transition-opacity duration-200 ${
              chatScrollEdges.bottom ? 'opacity-100' : 'opacity-0'
            }`}
          />
        </div>

        {/* New-UI empty state — heading shown when flag is on and no messages. */}
        {newUi && isEmpty && (
          <>
            <div className="flex flex-col items-center pb-4">
              <h2
                className="text-center"
                style={{
                  color: 'var(--text-1)',
                  fontSize: '2.5rem',
                  fontWeight: 300,
                  letterSpacing: '-0.015em',
                }}
              >
                What should we make?
              </h2>
            </div>
          </>
        )}

        {/* Input — Figma Make-style pill bar */}
        {!passiveNorthStarChat && (
          <div
            className={`${compact ? 'px-4' : 'px-6'} pb-6 pt-2${newUi && !isArtifactPaneActive && interviewSlotActive ? ' hidden' : ''}`}
          >
            <div className={compact ? 'w-full' : 'mx-auto max-w-3xl'}>
              <div className="mb-1 flex justify-end">
                <VisibilityStrip spend={lastTurnSpend} showCost={showCostInChat} />
              </div>
              {/* Issue #246: `<BugDraftCard>` watches
            `<gameDir>/.foundry/bug-draft.json` via the `bug:get-draft`
            poll and surfaces the SKILL-assembled bug report for
            user review. Mounts above the input strip — the same
            visual band as the queued-message chips and pasted-image
            chips, so the user sees pending-confirmation surfaces in
            one consistent place. Self-unmounts when the draft file
            is gone (success path or explicit cancel). */}
              {gameId && <BugDraftCard gameId={gameId} enabled={localSidecarsAvailable} />}
              {/* Issue #592: `<ConflictResolutionCard>` watches
            `<gameDir>/.foundry/pending-conflicts.json` via the
            `conflicts:get-pending` poll and surfaces the
            agentic Resolve-with-AI flow when `crucible update`
            (no flag) leaves conflict markers. Mounts in the same
            band as `<BugDraftCard>` so all pending-confirmation
            surfaces are visually consolidated. Self-unmounts when
            main deletes the sidecar (option β cleanup hook fires
            after the agent's edits, or user clicks "Resolve
            manually"). */}
              {gameId && (
                <ConflictResolutionCard gameId={gameId} enabled={localSidecarsAvailable} />
              )}
              {gameId && showReviewModal && (
                <ReviewAnswersModal gameId={gameId} onClose={onCloseReviewModal} />
              )}
              {newUi && !isArtifactPaneActive && pinnedAskUserQuestion && (
                <div
                  className="mb-2 max-h-[min(24rem,42vh)] overflow-y-auto overscroll-contain"
                  data-testid="pinned-ask-user-question"
                >
                  <AskUserQuestionCard toolCall={pinnedAskUserQuestion} />
                </div>
              )}
              {showPendingPhaseBDecisionCard && pendingPhaseBDecision && (
                <div
                  className="mb-2 max-h-[min(24rem,42vh)] overflow-y-auto overscroll-contain"
                  data-testid="pending-phase-b-decision"
                >
                  <PendingPhaseBDecisionCard
                    decision={pendingPhaseBDecision}
                    answered={answeredPhaseBDecisionId === pendingPhaseBDecision.intentId}
                    onAnswer={handlePhaseBDecisionAnswer}
                  />
                </div>
              )}
              {statusRow && (
                <div
                  className="mb-2 flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-white/60"
                  data-testid="chat-status-row"
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      statusRow.tone === 'active'
                        ? 'animate-pulse bg-crucible-accent'
                        : statusRow.tone === 'waiting' || statusRow.tone === 'warning'
                          ? 'bg-amber-300'
                          : 'bg-white/30'
                    }`}
                    aria-hidden="true"
                  />
                  <span>{statusRow.label}</span>
                </div>
              )}
              {queuedMessages.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-1.5" data-testid="chat-queue-chips">
                  {queuedMessages.map((msg, idx) => (
                    <Chip
                      key={idx}
                      variant="interactive"
                      onClick={() => onUnqueueMessage(idx)}
                      title="Click to remove from queue"
                      className="group flex max-w-[24rem] items-center gap-1.5 rounded-full border border-crucible-accent/30 bg-crucible-accent/10 px-3 py-1 text-xs text-white/70 transition-colors hover:border-red-500/50 hover:bg-red-500/10 hover:text-red-200"
                    >
                      <span className="text-[10px] uppercase tracking-wider text-white/40 group-hover:text-red-300/60">
                        Queued
                      </span>
                      <span className="truncate">{msg.text}</span>
                      {msg.attachments.length > 0 && (
                        <span
                          className="flex shrink-0 items-center gap-0.5 text-white/40 group-hover:text-red-300/60"
                          data-testid="chat-queue-chip-attachments"
                          aria-label={`${msg.attachments.length} attached image${msg.attachments.length > 1 ? 's' : ''}`}
                        >
                          <Paperclip className="h-3 w-3" aria-hidden="true" />
                          {msg.attachments.length}
                        </span>
                      )}
                      <span className="text-white/30 group-hover:text-red-300/80">×</span>
                    </Chip>
                  ))}
                </div>
              )}
              {/* Screenshot-paste MVP (#193) → thumbnail rework: image
            thumbnails (the pixels ARE the information; byte size
            dropped) with a hover/focus popover carrying the larger
            exact-aspect preview + the #193 redaction note, which used
            to be an always-visible banner here. Renders directly
            above the textarea so the user can see what's about to
            ride with their next send. */}
              {pendingAttachments.length > 0 && (
                <div
                  className="mb-2 flex flex-wrap items-center gap-1.5"
                  data-testid="chat-attachment-chips"
                >
                  {pendingAttachments.map((att, idx) => {
                    // Pop ONLY the just-added (last) thumbnail, not the whole row.
                    const popLatest = attachmentPop && idx === pendingAttachments.length - 1;
                    return (
                      <span
                        key={idx}
                        className={popLatest ? 'inline-flex animate-selection-pop' : 'inline-flex'}
                      >
                        <AttachmentThumbnail
                          attachment={att}
                          onRemove={() => onRemoveAttachment(idx)}
                        />
                      </span>
                    );
                  })}
                  {/* Single row-level privacy disclosure — global/static
                    info shown once, not per thumbnail. */}
                  <AttachmentRedactionInfo />
                </div>
              )}
              {attachmentError && (
                <div
                  className="mb-2 flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200"
                  data-testid="chat-attachment-error"
                >
                  <span className="flex-1">{attachmentError}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    type="button"
                    onClick={onDismissAttachmentError}
                    className="h-5 w-5 text-red-200/70 hover:text-red-100"
                    aria-label="Dismiss attachment error"
                  >
                    ×
                  </Button>
                </div>
              )}
              {/* #2992 sweep — voice-input failure banner (mic permission
                  denied / no device), mirroring the attachment-error idiom
                  directly above the composer. */}
              {voiceError && (
                <div
                  role="alert"
                  className="mb-2 flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200"
                  data-testid="chat-voice-error"
                >
                  <span className="flex-1">{voiceError}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    type="button"
                    onClick={() => setVoiceError(null)}
                    className="h-5 w-5 text-red-200/70 hover:text-red-100"
                    aria-label="Dismiss voice input error"
                  >
                    ×
                  </Button>
                </div>
              )}
              {/* #3060 — failed composer gate-answer banner (the answer invoke
                  rejected / returned not-ok; the typed text is preserved).
                  Same red role="alert" composer-error idiom as voice/attachment. */}
              {answerError && (
                <div
                  role="alert"
                  className="mb-2 flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200"
                  data-testid="chat-answer-error"
                >
                  <span className="flex-1">{answerError}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    type="button"
                    onClick={() => onDismissAnswerError?.()}
                    className="h-5 w-5 text-red-200/70 hover:text-red-100"
                    aria-label="Dismiss answer error"
                  >
                    ×
                  </Button>
                </div>
              )}
              {/* Hidden file input — triggered by the + button */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void resizeImageFile(file).then((resized) => onAddAttachment(resized));
                  e.currentTarget.value = '';
                }}
              />

              {/* Chat input — pill when single-line, Gemini-style rectangle when multi-line.
            The layout transitions the moment text wraps past one line. */}
              {isMultiLine ? (
                /* ── Expanded rectangle layout ── */
                <div
                  key="composer-multiline"
                  ref={composerWrapRef}
                  data-input-primitive="voice-first-text-fallback"
                  // Distinct key from the single-line branch so React MOUNTS a
                  // fresh node instead of reusing the pill's <div> — otherwise
                  // `transition` would animate the corner radius from the pill's
                  // full radius down to the fixed one (the brief small→large
                  // flicker). `transition-colors` also keeps border-radius out of
                  // any transition entirely.
                  className="chat-composer-surface flex flex-col rounded-[26px] transition-colors"
                  style={pillRadius != null ? { borderRadius: pillRadius } : undefined}
                  onDragOver={handleComposerDragOver}
                  onDrop={handleComposerDrop}
                >
                  {/* Textarea — grows up to ~160px, then scrolls */}
                  <textarea
                    ref={textareaRef}
                    value={chatInput}
                    onChange={(e) => onInputChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    // Wide (full-screen, no in-editor preview) keeps the longer
                    // hint with the screenshot affordance; the narrow split
                    // composer trims it so it fits the slimmer column.
                    placeholder={
                      compact
                        ? 'Tell the AI what to build…'
                        : 'Tell the AI what to build, or paste a screenshot…'
                    }
                    rows={1}
                    className="resize-none overflow-y-auto border-0 bg-transparent px-4 pt-3.5 pb-1 text-[15px] leading-relaxed placeholder:text-white/30 outline-none"
                    style={{ color: 'var(--text-1)' }}
                  />
                  {/* Bottom toolbar */}
                  <div className="flex items-center justify-between px-3 pb-2.5 pt-1">
                    <div className="flex items-center gap-1">
                      {/* Attach — bare at rest, flat circle on hover */}
                      <Button
                        variant="ghost"
                        size="icon"
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        aria-label="Attach an image"
                        className="h-8 w-8"
                        style={{ color: 'var(--text-3)' }}
                      >
                        <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                          <path
                            d="M6.5 1v11M1 6.5h11"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        type="button"
                        title={voiceSupported ? 'Voice message' : 'Type message'}
                        aria-label="Voice message"
                        aria-pressed={voiceListening}
                        data-testid="chat-voice-button"
                        onClick={handleVoiceInput}
                        className={`h-8 w-8 ${voiceListening ? 'bg-crucible-accent text-black' : ''}`}
                        style={{ color: voiceListening ? undefined : 'var(--text-3)' }}
                      >
                        <Mic size={16} strokeWidth={1.75} />
                      </Button>
                    </div>
                    {isAiTyping ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={onStop}
                        type="button"
                        aria-label="Stop the agent"
                        title="Stop the agent"
                        className="bg-red-500/80 text-white hover:bg-red-500"
                      >
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
                          <rect width="10" height="10" rx="1.5" />
                        </svg>
                      </Button>
                    ) : (
                      /* Expanded layout is always armed (text required to reach multi-line) */
                      <Button
                        variant="ghost"
                        size="icon"
                        type="button"
                        onClick={onSend}
                        data-testid="chat-send-button"
                        data-armed="true"
                        aria-label="Send message"
                        title="Send message"
                        className="bg-crucible-accent/20 text-crucible-accent-hover hover:bg-crucible-accent/30 hover:text-white disabled:opacity-30"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M8 13V3M3 8l5-5 5 5" />
                        </svg>
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                /* ── Compact pill layout (single line) ── */
                <div
                  key="composer-single"
                  ref={composerWrapRef}
                  data-input-primitive="voice-first-text-fallback"
                  className="chat-composer-surface flex items-center gap-3 rounded-full px-3 py-3"
                  onDragOver={handleComposerDragOver}
                  onDrop={handleComposerDrop}
                >
                  {/* Attach — bare at rest, flat circle on hover */}
                  <Button
                    variant="ghost"
                    size="icon"
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    aria-label="Attach an image"
                    className="shrink-0 h-8 w-8"
                    style={{ color: 'var(--text-3)' }}
                  >
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                      <path
                        d="M6.5 1v11M1 6.5h11"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </Button>
                  {/* Textarea */}
                  <textarea
                    ref={textareaRef}
                    value={chatInput}
                    onChange={(e) => onInputChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    // See the expanded-layout twin: long hint on the wide
                    // composer, trimmed hint on the narrow live-preview split.
                    placeholder={
                      compact
                        ? 'Tell the AI what to build…'
                        : 'Tell the AI what to build, or paste a screenshot…'
                    }
                    rows={1}
                    className="flex-1 resize-none overflow-hidden border-0 bg-transparent text-[15px] leading-relaxed placeholder:text-white/30 outline-none"
                    style={{ color: 'var(--text-1)' }}
                  />
                  {!!(chatInput.trim() || pendingAttachments.length > 0) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      type="button"
                      title={voiceSupported ? 'Voice message' : 'Type message'}
                      aria-label="Voice message"
                      aria-pressed={voiceListening}
                      data-testid="chat-voice-button"
                      onClick={handleVoiceInput}
                      className={`shrink-0 ${voiceListening ? 'bg-crucible-accent text-black' : ''}`}
                      style={{ color: voiceListening ? undefined : 'var(--text-3)' }}
                    >
                      <Mic size={16} strokeWidth={1.75} />
                    </Button>
                  )}
                  {/* Right icon: stop when busy; armed arrow (with bg) or bare ⚡ otherwise */}
                  {isAiTyping ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={onStop}
                      type="button"
                      aria-label="Stop the agent"
                      title="Stop the agent"
                      className="shrink-0 bg-red-500/80 text-white hover:bg-red-500"
                    >
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
                        <rect width="10" height="10" rx="1.5" />
                      </svg>
                    </Button>
                  ) : (
                    (() => {
                      const armed = !!(chatInput.trim() || pendingAttachments.length > 0);
                      return armed ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          type="button"
                          onClick={onSend}
                          data-testid="chat-send-button"
                          data-armed="true"
                          aria-label="Send message"
                          title="Send message"
                          className="shrink-0 bg-crucible-accent/20 text-crucible-accent-hover hover:bg-crucible-accent/30 hover:text-white"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M8 13V3M3 8l5-5 5 5" />
                          </svg>
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          type="button"
                          title={voiceSupported ? 'Voice message' : 'Type message'}
                          aria-label="Voice message"
                          aria-pressed={voiceListening}
                          data-testid="chat-send-button"
                          data-armed="false"
                          onClick={handleVoiceInput}
                          className={`shrink-0 ${
                            voiceListening ? 'bg-crucible-accent text-black' : ''
                          }`}
                          style={{ color: voiceListening ? undefined : 'var(--text-3)' }}
                        >
                          <Mic size={16} strokeWidth={1.75} />
                        </Button>
                      );
                    })()
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </ChatErrorBoundary>
    </section>
  );
}

/**
 * #2575 — ChatPanel is memoised so an unrelated Workspace re-render (a
 * keystroke elsewhere, a state flip that doesn't touch chat data) does not
 * re-render the whole chat subtree — including every ChatBubble. This is
 * only effective because Workspace now passes referentially-stable props:
 * every callback is a `useCallback` and `queuedMessages` is the reducer's
 * own `readonly QueuedChatMessage[]` (identity stable unless the queue
 * mutates). The
 * default shallow prop comparison is correct here; a custom comparator
 * would have to enumerate ~50 props and drift as they change, so we rely on
 * the stable-props discipline at the call site instead.
 */
export const ChatPanel = React.memo(ChatPanelImpl);
ChatPanel.displayName = 'ChatPanel';

/**
 * Inline placeholder shown in place of the AI Chat panel when no Anthropic
 * key is configured. Replaces the previous "render nothing" branch (audit
 * §4: users landed on the Workspace and saw an empty centre column with
 * no clue why). The card is keyboard-accessible — the button is a real
 * `<button>` (focusable, Enter/Space activatable), it has a focus ring,
 * and the heading + button each carry an explicit aria label.
 */
export function ChatPanelPlaceholder({
  onOpenSettings,
  persistError,
  onDismissPersistError,
}: {
  onOpenSettings: () => void;
  persistError?: SerialisedError | null;
  onDismissPersistError?: () => void;
}): React.JSX.Element {
  return (
    <section
      aria-labelledby="chat-placeholder-heading"
      className="flex flex-1 flex-col items-center justify-center overflow-hidden bg-crucible-bg px-6 py-10"
    >
      {persistError && (
        <div className="mb-4 w-full max-w-md" data-testid="chat-persist-error">
          <CrucibleErrorState error={persistError} compact />
          {onDismissPersistError && (
            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={onDismissPersistError}
              className="mt-2 h-auto p-0 text-xs text-white/40 underline-offset-2 hover:underline font-normal"
            >
              Dismiss
            </Button>
          )}
        </div>
      )}
      <div className="max-w-md rounded-xl border border-white/10 bg-crucible-surface p-6 text-center shadow-sm">
        <h2 id="chat-placeholder-heading" className="mb-2 text-base font-semibold text-white/80">
          AI assistant needs setup
        </h2>
        <p className="mb-5 text-sm leading-relaxed text-white/50">
          Foundry uses Claude to help build your game. Add your Anthropic API key in Settings to
          start.
        </p>
        <Button
          type="button"
          onClick={onOpenSettings}
          aria-label="Open Settings to add your Anthropic API key"
          className="inline-flex items-center gap-2 rounded-lg"
        >
          Open Settings
          <span aria-hidden="true">→</span>
        </Button>
      </div>
    </section>
  );
}
